/* ==========================================================================
   Start Nutra · Header behaviour
   --------------------------------------------------------------------------
   SnHeader      sticky shadow (elevation trigger past the scroll threshold)
   SnMenuSheet   open/close + focus trap + Escape + body scroll lock
   ========================================================================== */

class SnHeader {
  constructor() {
    // State class goes on the section WRAPPER (its containing block is <body>,
    // so sticky travels the whole page). See section-sn-header.css.
    this.section = document.querySelector('.sn-header-section');
    this.header = document.querySelector('.sn-header');
    if (!this.section || !this.header) return;
    this.threshold = 80;
    this.bindScroll();
    this.handleScroll();
  }

  bindScroll() {
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        this.handleScroll();
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }

  handleScroll() {
    const y = window.scrollY;

    // Shadow once scrolled past the threshold, never at rest.
    this.section.classList.toggle('sn-header--scrolled', y > this.threshold);
  }
}

class SnMenuSheet {
  constructor() {
    this.sheet = document.querySelector('.sn-menu-sheet');
    this.trigger = document.querySelector('.sn-header__hamburger');
    if (!this.sheet || !this.trigger) return;
    this.panel = this.sheet.querySelector('.sn-menu-sheet__panel');
    this.closeBtn = this.sheet.querySelector('.sn-menu-sheet__close');
    this.backdrop = this.sheet.querySelector('.sn-menu-sheet__backdrop');
    this.focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.bind();
  }

  bind() {
    this.trigger.addEventListener('click', () => this.open());
    this.closeBtn.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (this.sheet.dataset.open !== 'true') return;
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    });
  }

  open() {
    this.sheet.hidden = false;
    // next frame so transitions trigger
    requestAnimationFrame(() => {
      this.sheet.dataset.open = 'true';
      this.sheet.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.closeBtn.focus();
    });
  }

  close() {
    this.sheet.dataset.open = 'false';
    this.sheet.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    // wait for the slide-out transition before hiding
    setTimeout(() => {
      if (this.sheet.dataset.open !== 'true') {
        this.sheet.hidden = true;
      }
      this.trigger.focus();
    }, 300);
  }

  trapFocus(e) {
    const focusables = this.panel.querySelectorAll(this.focusableSelector);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }
}

// Hover-intent delay for dropdown open/close, see SnDropdowns.bind(). No
// prior delay mechanism existed (hover was pure CSS :hover/:focus-within,
// instant); this adds one rather than tuning an existing value.
const HOVER_INTENT_MS = 150;

class SnDropdowns {
  constructor() {
    this.triggers = Array.from(document.querySelectorAll('[data-sn-dropdown-trigger]'));
    if (!this.triggers.length) return;
    this.hoverTimer = null;
    this.bind();
  }

  bind() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.closeAll();
        if (!isOpen) trigger.setAttribute('aria-expanded', 'true');
      });

      // Hover intent: moving from trigger to panel (or briefly off either)
      // keeps the dropdown open for HOVER_INTENT_MS before it auto-closes,
      // instead of closing the instant the pointer leaves. The panel is
      // always the trigger's next sibling (matches the CSS "+" selector
      // that already governs its visibility).
      const panel = trigger.nextElementSibling;

      trigger.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverTimer);
        if (trigger.getAttribute('aria-expanded') !== 'true') this.open(trigger);
      });

      trigger.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => this.closeAll(), HOVER_INTENT_MS);
      });

      if (panel) {
        panel.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimer);
          if (trigger.getAttribute('aria-expanded') !== 'true') this.open(trigger);
        });

        panel.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.closeAll(), HOVER_INTENT_MS);
        });
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.sn-nav-item--has-children')) this.closeAll();
    });

    // Close any open dropdown when focus leaves the nav (keyboard tab away).
    const desktopNav = document.querySelector('.sn-header__nav-desktop');
    if (desktopNav) {
      desktopNav.addEventListener('focusout', (e) => {
        if (!desktopNav.contains(e.relatedTarget)) this.closeAll();
      });
    }

    // SnDropdowns is a plain class, not a custom element, no
    // disconnectedCallback exists to clear the timer on teardown, so
    // pagehide is the equivalent single guard.
    window.addEventListener('pagehide', () => clearTimeout(this.hoverTimer));
  }

  // Used only by the new hover listeners above; the click handler keeps
  // its own inline open logic untouched, per scope.
  open(trigger) {
    this.closeAll();
    trigger.setAttribute('aria-expanded', 'true');
  }

  closeAll() {
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }
}

function snInitHeader() {
  new SnHeader();
  new SnMenuSheet();
  new SnDropdowns();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', snInitHeader);
} else {
  snInitHeader();
}
